import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaMoneyBillTransfer, FaFilter } from 'react-icons/fa6';
import { FiCheckCircle, FiXCircle, FiAlertTriangle, FiPrinter, FiDownload, FiX, FiFileText } from 'react-icons/fi';
import { transactionsService } from '../services';
import type { Transaction, TransactionFilters, TransactionReceipt } from '../types';
import { Card, LoaderComponent, EmptyState, Button, Modal } from '../components/ui';

type TabKey = 'all' | 'paid' | 'pending' | 'failed';

const isPaid = (e: string) => ['Pago', 'PAID', 'PG', 'PAGO'].includes(e);
const isPending = (e: string) => ['Pendente', 'PENDING', 'PE', 'Emitido', 'NPAGO', 'Rascunho', 'RASC'].includes(e);
const isFailed = (e: string) => ['Falhado', 'FALHOU', 'FAILED', 'FL', 'CANCELLED', 'CN', 'Cancelado'].includes(e);

const TAB_CONFIG: { key: TabKey; label: string; icon: React.ReactNode; match: (estado: string) => boolean }[] = [
  { key: 'paid', label: 'Pagos', icon: <FiCheckCircle className="text-green-500" />, match: isPaid },
  { key: 'pending', label: 'Não Concluído', icon: <FiAlertTriangle className="text-yellow-500" />, match: isPending },
  { key: 'failed', label: 'Falhados', icon: <FiXCircle className="text-red-500" />, match: isFailed },
  { key: 'all', label: 'Todos', icon: null, match: () => true },
];

// ─── Receipt Modal ───
const ReceiptModal: React.FC<{
  receipt: TransactionReceipt | null;
  transaction: Transaction | null;
  loading: boolean;
  onClose: () => void;
}> = ({ receipt, transaction, loading, onClose }) => {
  const printRef = useRef<HTMLDivElement>(null);

  const handlePrint = () => {
    if (!printRef.current) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    printWindow.document.write(`
      <html><head><title>Comprovativo de Pagamento</title>
      <style>
        body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 40px; max-width: 500px; margin: 0 auto; color: #1f2937; }
        .header { text-align: center; border-bottom: 2px solid #6233A0; padding-bottom: 16px; margin-bottom: 24px; }
        .header h1 { font-size: 18px; color: #6233A0; margin: 0 0 4px; }
        .header p { font-size: 12px; color: #6b7280; margin: 0; }
        .row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #f3f4f6; }
        .row .label { color: #6b7280; font-size: 13px; }
        .row .value { font-weight: 600; font-size: 13px; }
        .total { font-size: 24px; font-weight: 700; text-align: center; margin: 24px 0; color: #059669; }
        .status { text-align: center; padding: 8px 16px; border-radius: 20px; display: inline-block; font-size: 13px; font-weight: 600; }
        .status.success { background: #ecfdf5; color: #059669; }
        .footer { margin-top: 32px; padding-top: 16px; border-top: 1px solid #e5e7eb; text-align: center; font-size: 11px; color: #9ca3af; }
        .qr { text-align: center; margin: 16px 0; }
        @media print { body { padding: 20px; } }
      </style></head><body>
      ${printRef.current.innerHTML}
      <script>window.print(); window.close();</script>
      </body></html>
    `);
    printWindow.document.close();
  };

  if (!transaction) return null;

  const estado = transaction.estado || transaction.estadoId || 'N';
  const paid = isPaid(estado);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <FiFileText className="text-pagali-purple" /> Comprovativo
          </h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><FiX /></button>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <div className="w-8 h-8 border-2 border-pagali-purple/30 border-t-pagali-purple rounded-full animate-spin mx-auto mb-3" />
            <p className="text-gray-500 text-sm">A carregar comprovativo...</p>
          </div>
        ) : (
          <>
            {/* Printable content */}
            <div ref={printRef} className="p-6">
              {/* Logo / Title */}
              <div className="text-center border-b-2 border-pagali-purple pb-4 mb-6">
                <h1 className="text-lg font-bold text-pagali-purple">Comprovativo de Pagamento</h1>
                <p className="text-xs text-gray-500">Pagali - Plataforma de Pagamentos</p>
              </div>

              {/* Amount */}
              <div className="text-center mb-6">
                <p className="text-3xl font-bold text-green-600">
                  {new Intl.NumberFormat('pt-CV', { style: 'currency', currency: 'CVE', minimumFractionDigits: 0 }).format(
                    receipt?.valor || transaction.valor || transaction.montante || 0
                  )}
                </p>
                <div className="mt-2">
                  {paid ? (
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-semibold bg-green-100 text-green-700">
                      <FiCheckCircle /> Pago
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-semibold bg-yellow-100 text-yellow-700">
                      <FiAlertTriangle /> Não Concluído
                    </span>
                  )}
                </div>
              </div>

              {/* Details */}
              <div className="space-y-0">
                {receipt?.numero && (
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-500">N.º Comprovativo</span>
                    <span className="text-sm font-semibold font-mono">{receipt.numero}</span>
                  </div>
                )}
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-500">Entidade</span>
                  <span className="text-sm font-semibold">{receipt?.entidade || transaction.entidadeNome || transaction.entidade || transaction.servico || '-'}</span>
                </div>
                {(receipt?.referenciaServico || transaction.referencia) && (
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-500">
                      {receipt?.tipoServico === 'RCG' || receipt?.tipoServico === '3' ? 'N.º Telemovel / Contador' : 'Referência'}
                    </span>
                    <span className="text-sm font-semibold font-mono">{receipt?.referenciaServico || transaction.referencia}</span>
                  </div>
                )}
                {receipt?.tokenRecarga && (
                  <div className="py-3 border-b border-gray-100">
                    <span className="text-sm text-gray-500 block mb-2">Token de Recarga</span>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                      <span className="text-lg font-mono font-bold text-green-800 tracking-wider select-all">
                        {receipt.tokenRecarga}
                      </span>
                      <p className="text-xs text-green-600 mt-1">Introduza este token no seu contador</p>
                    </div>
                  </div>
                )}
                {receipt?.talao && (
                  <div className="py-3 border-b border-gray-100">
                    <span className="text-sm text-gray-500 block mb-2">Talão da Recarga</span>
                    <pre className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs font-mono text-gray-700 whitespace-pre-wrap overflow-x-auto">{receipt.talao}</pre>
                  </div>
                )}
                {receipt?.descricao && (
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-500">Descrição</span>
                    <span className="text-sm font-semibold">{receipt.descricao}</span>
                  </div>
                )}
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-500">Data</span>
                  <span className="text-sm font-semibold">
                    {new Date(receipt?.data || transaction.data).toLocaleDateString('pt-CV', {
                      timeZone: 'Atlantic/Cape_Verde',
                      day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
                    })}
                  </span>
                </div>
                {receipt?.cliente?.nome && (
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-500">Cliente</span>
                    <span className="text-sm font-semibold">{receipt.cliente.nome}</span>
                  </div>
                )}
                {receipt?.cliente?.nif && (
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-500">NIF</span>
                    <span className="text-sm font-semibold font-mono">{receipt.cliente.nif}</span>
                  </div>
                )}
                {receipt?.merchantRef && (
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-500">Ref. Transação</span>
                    <span className="text-xs font-mono text-gray-600">{receipt.merchantRef}</span>
                  </div>
                )}
              </div>

              {/* QR Code */}
              {receipt?.qrCode && (
                <div className="text-center mt-4">
                  <img src={receipt.qrCode} alt="QR Code" className="w-24 h-24 mx-auto" />
                </div>
              )}

              {/* Disclaimer */}
              <div className="mt-6 pt-4 border-t border-gray-200 text-center">
                <p className="text-[11px] text-gray-400 leading-relaxed">
                  Este documento é um comprovativo de pagamento e não serve de fatura.
                  A fatura deverá ser emitida pela entidade prestadora do serviço.
                </p>
                <p className="text-[10px] text-gray-300 mt-2">
                  Pagali &copy; {new Date().getFullYear()} - Todos os direitos reservados
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="p-4 border-t border-gray-200 flex gap-3">
              <button onClick={handlePrint}
                className="flex-1 flex items-center justify-center gap-2 py-3 bg-pagali-purple text-white rounded-xl font-medium hover:bg-pagali-purple-light transition-colors">
                <FiPrinter /> Imprimir
              </button>
              <button onClick={handlePrint}
                className="flex-1 flex items-center justify-center gap-2 py-3 border-2 border-pagali-purple text-pagali-purple rounded-xl font-medium hover:bg-pagali-purple/5 transition-colors">
                <FiDownload /> Guardar PDF
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Mapeamento tab → sispStatus enviado ao servidor
const TAB_SISP: Record<TabKey, string | undefined> = {
  all: undefined,
  paid: 'N',
  pending: 'S',
  failed: 'F',
};

const PAGE_SIZE = 50;

const Transactions: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [filters, setFilters] = useState<TransactionFilters>({});
  const [tempFilters, setTempFilters] = useState<TransactionFilters>({});
  const [activeTab, setActiveTab] = useState<TabKey>('paid');
  const [receiptModalOpen, setReceiptModalOpen] = useState(false);
  const [receiptLoading, setReceiptLoading] = useState(false);
  const [receipt, setReceipt] = useState<TransactionReceipt | null>(null);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [autoOpenHandled, setAutoOpenHandled] = useState(false);

  useEffect(() => {
    sessionStorage.setItem('pageTitle', 'Transações');
    loadTransactions(true);
  }, [filters, activeTab]);

  // Auto-open transaction detail from Dashboard navigation
  useEffect(() => {
    if (!autoOpenHandled && !loading && transactions.length > 0 && location.state?.openTransaction) {
      const txId = location.state.openTransaction;
      const tx = transactions.find(t => t.id === txId);
      if (tx) {
        handleTransactionClick(tx);
      }
      setAutoOpenHandled(true);
      window.history.replaceState({}, document.title);
    }
  }, [loading, transactions, autoOpenHandled, location.state]);

  const loadTransactions = async (reset = false) => {
    if (reset) {
      setLoading(true);
      setOffset(0);
    } else {
      setLoadingMore(true);
    }
    try {
      const currentOffset = reset ? 0 : offset;
      const data = await transactionsService.getTransactions({
        ...filters,
        sispStatus: TAB_SISP[activeTab],
        limit: PAGE_SIZE,
        offset: currentOffset,
      } as any);
      const fetched = Array.isArray(data) ? data : [];
      setTransactions(prev => reset ? fetched : [...prev, ...fetched]);
      setOffset(currentOffset + fetched.length);
      setHasMore(fetched.length === PAGE_SIZE);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('transactions.errorLoading'));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Sem filtro client-side — servidor já filtrou pelo tab activo
  const filteredTransactions = transactions;

  // Contagem apenas para o tab activo (servidor filtra)
  const tabCounts = useMemo(() => {
    const counts: Record<TabKey, number> = { all: 0, paid: 0, pending: 0, failed: 0 };
    counts[activeTab] = transactions.length;
    return counts;
  }, [transactions, activeTab]);

  const formatAmount = (amount: number): string =>
    new Intl.NumberFormat('pt-CV', { style: 'currency', currency: 'CVE', minimumFractionDigits: 0 }).format(amount);

  const formatDate = (dateString: string): string =>
    new Date(dateString).toLocaleDateString('pt-CV', { timeZone: 'Atlantic/Cape_Verde', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  const getStatusBadge = (estado: string): JSX.Element => {
    if (isPaid(estado))
      return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700"><FiCheckCircle size={12} /> Pago</span>;
    if (isPending(estado))
      return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700"><FiAlertTriangle size={12} /> Não Concluído</span>;
    if (isFailed(estado))
      return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700"><FiXCircle size={12} /> Falhou</span>;
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600"><FiAlertTriangle size={12} /> {estado}</span>;
  };

  const applyFilters = () => {
    setFilters(tempFilters);
    setFilterModalOpen(false);
  };

  const clearFilters = () => {
    setFilters({});
    setTempFilters({});
    setFilterModalOpen(false);
  };

  const hasActiveFilters = filters.startDate || filters.endDate;

  const handleTransactionClick = async (tx: Transaction) => {
    const estado = tx.estado || tx.estadoId || 'N';
    if (!isPaid(estado)) return;
    setSelectedTransaction(tx);
    setReceipt(null);
    setReceiptModalOpen(true);
    setReceiptLoading(true);
    try {
      const data = await transactionsService.getReceipt(tx.id);
      setReceipt(data);
    } catch {
      // Show receipt with transaction data only
    } finally {
      setReceiptLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-pagali-gray-800">{t('transactions.title')}</h2>
          <p className="text-pagali-gray-500">{t('transactions.subtitle')}</p>
        </div>
        <Button
          variant={hasActiveFilters ? 'primary' : 'secondary'}
          icon={<FaFilter />}
          onClick={() => { setTempFilters(filters); setFilterModalOpen(true); }}
        >
          {t('common.filter')}
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-pagali-gray-100 rounded-xl p-1">
        {TAB_CONFIG.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab.key
                ? 'bg-white text-pagali-primary shadow-sm'
                : 'text-pagali-gray-500 hover:text-pagali-gray-700'
            }`}
          >
            {tab.icon}
            {tab.label}
            <span className={`text-xs px-1.5 py-0.5 rounded-full ${
              activeTab === tab.key ? 'bg-pagali-primary/10 text-pagali-primary' : 'bg-pagali-gray-200 text-pagali-gray-500'
            }`}>
              {tabCounts[tab.key]}
            </span>
          </button>
        ))}
      </div>

      {/* Active Filters */}
      {hasActiveFilters && (
        <div className="flex flex-wrap gap-2">
          {filters.startDate && (
            <span className="bg-pagali-primary/10 text-pagali-primary px-3 py-1 rounded-full text-sm">
              {t('common.from')}: {filters.startDate}
            </span>
          )}
          {filters.endDate && (
            <span className="bg-pagali-primary/10 text-pagali-primary px-3 py-1 rounded-full text-sm">
              {t('common.to')}: {filters.endDate}
            </span>
          )}
          <button onClick={clearFilters} className="text-pagali-error text-sm hover:underline">
            {t('common.clearFilters')}
          </button>
        </div>
      )}

      {/* Content */}
      <Card padding="none">
        {loading ? (
          <div className="p-6">
            <LoaderComponent size="md" text={t('transactions.loadingTransactions')} />
          </div>
        ) : error ? (
          <div className="p-6 text-center">
            <p className="text-pagali-error mb-4">{error}</p>
            <Button onClick={loadTransactions}>{t('common.tryAgain')}</Button>
          </div>
        ) : filteredTransactions.length === 0 ? (
          <div className="p-6">
            <EmptyState
              icon={<FaMoneyBillTransfer className="text-5xl" />}
              title={activeTab === 'all' ? t('transactions.noTransactions') : `Sem transações ${TAB_CONFIG.find(t => t.key === activeTab)?.label.toLowerCase()}`}
              description={hasActiveFilters ? t('transactions.noTransactionsFiltered') : t('transactions.transactionsWillAppear')}
              action={hasActiveFilters ? <Button variant="secondary" onClick={clearFilters}>{t('common.clearFilters')}</Button> : undefined}
            />
          </div>
        ) : (
          <div>
            <div className="divide-y divide-pagali-gray-100">
              {filteredTransactions.map((transaction) => {
                const estado = transaction.estado || transaction.estadoId || 'N';
                const paid = isPaid(estado);
                return (
                  <div
                    key={transaction.id}
                    onClick={() => handleTransactionClick(transaction)}
                    className={`flex items-center justify-between p-4 hover:bg-pagali-gray-50 transition-colors ${paid ? 'cursor-pointer' : ''}`}
                  >
                    {/* Left */}
                    <div className="flex items-center gap-4">
                      {(transaction.logo || (transaction as any).entidadeLogo) ? (
                        <img
                          src={`data:${transaction.mimetype || (transaction as any).entidadeMime || 'image/png'};base64,${transaction.logo || (transaction as any).entidadeLogo}`}
                          alt={transaction.entidade}
                          className="w-12 h-12 rounded-full object-contain bg-white ring-2 ring-pagali-gray-100 p-1"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-pagali-gray-200 flex items-center justify-center ring-2 ring-pagali-gray-100">
                          <FaMoneyBillTransfer className="text-xl text-pagali-gray-400" />
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-pagali-gray-800">
                          {transaction.entidadeNome || transaction.entidade || transaction.servico || 'Pagamento'}
                        </p>
                        <p className="text-sm text-pagali-gray-500">
                          {formatDate(transaction.data)}
                        </p>
                        {transaction.referencia && (
                          <p className="text-xs text-pagali-gray-400 mt-0.5 font-mono">
                            Ref: {transaction.referencia}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Right */}
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="font-semibold text-lg text-pagali-gray-800">
                          {formatAmount(transaction.valor || transaction.montante || 0)}
                        </p>
                        {getStatusBadge(estado)}
                      </div>
                      {paid && (
                        <FiFileText className="text-pagali-purple flex-shrink-0" title="Ver comprovativo" />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Load more */}
            {hasMore && (
              <div className="p-4 text-center border-t border-pagali-gray-100">
                <Button
                  variant="secondary"
                  onClick={() => loadTransactions(false)}
                  disabled={loadingMore}
                >
                  {loadingMore ? 'A carregar...' : 'Carregar mais'}
                </Button>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Receipt Modal */}
      {receiptModalOpen && (
        <ReceiptModal
          receipt={receipt}
          transaction={selectedTransaction}
          loading={receiptLoading}
          onClose={() => { setReceiptModalOpen(false); setSelectedTransaction(null); setReceipt(null); }}
        />
      )}

      {/* Filter Modal */}
      <Modal
        isOpen={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        title={t('transactions.filterTransactions')}
        footer={
          <div className="flex gap-3">
            <Button variant="ghost" onClick={clearFilters}>{t('common.clear')}</Button>
            <Button onClick={applyFilters}>{t('common.apply')}</Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="form-group">
              <label className="input-label">{t('transactions.startDate')}</label>
              <input
                type="date"
                value={tempFilters.startDate || ''}
                onChange={(e) => setTempFilters({ ...tempFilters, startDate: e.target.value })}
                className="input-field"
              />
            </div>
            <div className="form-group">
              <label className="input-label">{t('transactions.endDate')}</label>
              <input
                type="date"
                value={tempFilters.endDate || ''}
                onChange={(e) => setTempFilters({ ...tempFilters, endDate: e.target.value })}
                className="input-field"
              />
            </div>
          </div>
          <p className="text-xs text-pagali-gray-400">
            Use os separadores (Pagos / Não Concluído / Falhados / Todos) para filtrar por estado.
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default Transactions;
